import {
  type EnumeratedEnumVFrame,
  type FixedEnumVFrame,
  isFixedEnumVFrame,
} from '@festina/life-general-types/life'
import type {
  PartitionCommandSelection,
  SwitchSubPortfolioGroupCommandData,
  SwitchSubPortfolioGroupCommandDataMFrame,
} from '@festina/unitlink-general-types/unitlink'
import { notEmpty, type Optional, typeUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import {
  useFormikContext,
  useTranslate,
  useTranslateEnum,
} from '@festina-life/ui/hooks'
import { Modal } from '@festina-life/ui/structure'
import { useId, useState } from 'react'

import { LifeForm } from '../../forms/LifeForm'
import { LifeInput } from '../../inputs/LifeInput'
import { FundCommandSelectionTable } from './FundCommandSelectionTable'

const validateKey = typeUtils.createValidator<PartitionCommandSelection>()

type Props = {
  partitionCommandSelection: PartitionCommandSelection
  existingProfileTypes: string[]
  onClose: () => void
  onSubmit: (updatedPartition: PartitionCommandSelection) => void
  mFrame: SwitchSubPortfolioGroupCommandDataMFrame | null
}

export const EditPartitionCommandSelectionModal = ({
  partitionCommandSelection,
  existingProfileTypes,
  onClose,
  onSubmit,
  mFrame,
}: Props) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { values } = useFormikContext<SwitchSubPortfolioGroupCommandData>()
  const [profileType, setProfileType] = useState<Optional<string>>(
    partitionCommandSelection?.profileType,
  )

  const updateMFrame = mFrame?.updatePartitionCommandSelections?.find(
    (updatePartitionCommandSelectionMFrame) => {
      const profileTypeFrame =
        updatePartitionCommandSelectionMFrame?.profileType
      return (
        isFixedEnumVFrame(profileTypeFrame) &&
        profileTypeFrame.value === profileType
      )
    },
  )

  const isUpdate = !!partitionCommandSelection.profileType

  // If we are updating an existing partition, we only want to allow editing of the fraction
  const updateFrame = {
    _type: 'PartitionCommandSelectionFrame',
    profileType: {
      _type: 'FixedEnumVFrame',
      value: profileType,
    } satisfies FixedEnumVFrame,
    fraction: updateMFrame?.fraction,
  }

  // If we are adding a new partition, we want to use the addPartitionCommandSelections mFrames to populate the frame
  const addProfileValues =
    mFrame?.addPartitionCommandSelections
      ?.map((addPartitionCommandSelectionMFrame) => {
        const profileTypeFrame = addPartitionCommandSelectionMFrame?.profileType
        return isFixedEnumVFrame(profileTypeFrame)
          ? profileTypeFrame.value
          : undefined
      })
      .filter(notEmpty) ?? []

  const addProfileFrame: EnumeratedEnumVFrame = {
    _type: 'EnumeratedEnumVFrame',
    defaultValue: profileType,
    values: addProfileValues.filter(
      (profile) => !existingProfileTypes.includes(profile), // can't add a profile that has already been added
    ),
  }

  const addMFrame = mFrame?.addPartitionCommandSelections?.find(
    (addPartitionCommandSelectionMFrame) => {
      const profileTypeFrame = addPartitionCommandSelectionMFrame?.profileType
      return (
        isFixedEnumVFrame(profileTypeFrame) &&
        profileTypeFrame.value === profileType
      )
    },
  )

  const addFrame = {
    _type: 'PartitionCommandSelectionFrame',
    profileType: addProfileFrame,
    fraction: addMFrame?.fraction,
  }

  const frame = isUpdate ? updateFrame : addFrame

  const initialValue = values.partitionCommandSelections?.find(
    (selection) => selection?.profileType === profileType,
  ) ?? { _type: 'PartitionCommandSelection', profileType }

  const formId = useId()

  return (
    <Modal onClose={onClose} isOpen>
      <Modal.Title
        title={translate('editPartitionCommandSelectionModal.partition')}
      />
      <Modal.Body>
        <LifeForm
          formId={formId}
          initialValues={initialValue || { _type: 'PartitionCommandSelection' }}
          onSubmit={onSubmit}
          frame={frame}
        >
          <LifeInput
            fallbackInputType="string"
            name={validateKey('profileType')}
            label={translate('editPartitionCommandSelectionModal.profileType')}
            onBlur={setProfileType}
            renderFn={(value) =>
              translateEnum('unitLink.enum.ProfileType', value)
            }
            disabled={isUpdate}
          />
          <LifeInput
            fallbackInputType="percentage"
            name={validateKey('fraction')}
            label={translate('editPartitionCommandSelectionModal.fraction')}
          />
        </LifeForm>
        <FundCommandSelectionTable
          mFrame={isUpdate ? updateMFrame : addMFrame}
          partitionCommandSelection={initialValue}
          title={translate('fundCommandSelectionTable.fundsTableTitle', {
            profileType:
              translateEnum('unitLink.enum.ProfileType', profileType) ?? '-',
          })}
        />
        <Modal.Footer>
          <Button variant="discreet" onClick={onClose}>
            {translate('cancel')}
          </Button>
          <Button type="submit" form={formId}>
            {translate('save')}
          </Button>
        </Modal.Footer>
      </Modal.Body>
    </Modal>
  )
}
